import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DialogFooter } from "@/components/ui/dialog";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export function EditProfileForm({ onComplete }) {
  const [profileData, setProfileData] = useState({
    business_description: "",
    target_audience: "",
    services_offered: "",
    services_needed: "",
    referral_partners_wanted: "",
    booking_link: "",
    revenue_stage: ""
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/community/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          business_description: profileData.business_description,
          target_audience: profileData.target_audience,
          services_offered: profileData.services_offered.split(",").map(s => s.trim()).filter(Boolean),
          services_needed: profileData.services_needed.split(",").map(s => s.trim()).filter(Boolean),
          referral_partners_wanted: profileData.referral_partners_wanted,
          booking_link: profileData.booking_link,
          revenue_stage: profileData.revenue_stage
        })
      });
      if (res.ok) {
        toast.success("Profile updated!");
        onComplete();
      } else {
        toast.error("Failed to update profile");
      }
    } catch (error) {
      toast.error("Error updating profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 pt-2">
      <div>
        <Label className="text-neutral-300">Business Description</Label>
        <Textarea
          value={profileData.business_description}
          onChange={(e) => setProfileData({...profileData, business_description: e.target.value})}
          placeholder="Describe what your business does..."
          className="bg-neutral-900 border-neutral-700"
          data-testid="profile-business-description"
        />
      </div>
      <div>
        <Label className="text-neutral-300">Target Audience</Label>
        <Input
          value={profileData.target_audience}
          onChange={(e) => setProfileData({...profileData, target_audience: e.target.value})}
          placeholder="Who do you serve?"
          className="bg-neutral-900 border-neutral-700"
        />
      </div>
      <div>
        <Label className="text-neutral-300">Services Offered (comma separated)</Label>
        <Input
          value={profileData.services_offered}
          onChange={(e) => setProfileData({...profileData, services_offered: e.target.value})}
          placeholder="e.g., Marketing, Consulting, Web Design"
          className="bg-neutral-900 border-neutral-700"
        />
      </div>
      <div>
        <Label className="text-neutral-300">Services Needed (comma separated)</Label>
        <Input
          value={profileData.services_needed}
          onChange={(e) => setProfileData({...profileData, services_needed: e.target.value})}
          placeholder="e.g., Accounting, Legal, HR"
          className="bg-neutral-900 border-neutral-700"
        />
      </div>
      <div>
        <Label className="text-neutral-300">Revenue Stage</Label>
        <Select 
          value={profileData.revenue_stage} 
          onValueChange={(v) => setProfileData({...profileData, revenue_stage: v})}
        >
          <SelectTrigger className="bg-neutral-900 border-neutral-700">
            <SelectValue placeholder="Select your stage" />
          </SelectTrigger>
          <SelectContent className="bg-neutral-800 border-neutral-700">
            <SelectItem value="pre_revenue">Pre-Revenue</SelectItem>
            <SelectItem value="under_100k">Under $100K</SelectItem>
            <SelectItem value="100k_500k">$100K - $500K</SelectItem>
            <SelectItem value="500k_1m">$500K - $1M</SelectItem>
            <SelectItem value="1m_5m">$1M - $5M</SelectItem>
            <SelectItem value="5m_plus">$5M+</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-neutral-300">Booking Link (optional)</Label>
        <Input
          value={profileData.booking_link}
          onChange={(e) => setProfileData({...profileData, booking_link: e.target.value})}
          placeholder="e.g., https://calendly.com/yourname"
          className="bg-neutral-900 border-neutral-700"
        />
      </div>
      <DialogFooter className="mt-4">
        <Button 
          onClick={handleSave}
          className="bg-[#39FF14] text-black hover:bg-[#32e612] w-full"
          disabled={!profileData.business_description || saving}
          data-testid="save-profile-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Save Profile
        </Button>
      </DialogFooter>
    </div>
  );
}
